export type IconName =
  | 'cog'
  | 'back'
  | 'chev'
  | 'share'
  | 'copy'
  | 'qr'
  | 'apple'
  | 'swords'
  | 'target'
  | 'chart'
  | 'history'
  | 'retry'
  | 'flame'
  | 'star'
  | 'close'
  | 'plus'
  | 'warn'

export interface IconProps {
  name: IconName
  size?: number
  color?: string
}

export function Icon({ name, size = 20, color = 'currentColor' }: IconProps) {
  const svgProps = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: color,
    strokeWidth: 1.6,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  }

  switch (name) {
    case 'cog':
      return (
        <svg {...svgProps}>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33 1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82 1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      )
    case 'back':
      return (
        <svg {...svgProps}>
          <polyline points="15 18 9 12 15 6" />
        </svg>
      )
    case 'chev':
      return (
        <svg {...svgProps}>
          <polyline points="9 6 15 12 9 18" />
        </svg>
      )
    case 'share':
      return (
        <svg {...svgProps}>
          <circle cx="18" cy="5" r="3" />
          <circle cx="6" cy="12" r="3" />
          <circle cx="18" cy="19" r="3" />
          <line x1="8.6" y1="13.5" x2="15.4" y2="17.5" />
          <line x1="15.4" y1="6.5" x2="8.6" y2="10.5" />
        </svg>
      )
    case 'copy':
      return (
        <svg {...svgProps}>
          <rect x="9" y="9" width="12" height="12" rx="2" />
          <path d="M5 15V5a2 2 0 0 1 2-2h10" />
        </svg>
      )
    case 'qr':
      return (
        <svg {...svgProps}>
          <rect x="3" y="3" width="6" height="6" />
          <rect x="15" y="3" width="6" height="6" />
          <rect x="3" y="15" width="6" height="6" />
          <line x1="12" y1="12" x2="12" y2="18" />
          <line x1="15" y1="15" x2="21" y2="15" />
        </svg>
      )
    case 'apple':
      return (
        <svg {...svgProps}>
          <path d="M12 6 C 7 6 4 10 4 14 c0 4 3 6 5 6 c1 0 2-0.5 3-0.5 s2 0.5 3 0.5 c2 0 5-2 5-6 c0-4-3-8-8-8 z" />
          <path d="M12 6 C 13 4 14 3 14 2" />
        </svg>
      )
    case 'swords':
      return (
        <svg {...svgProps}>
          <path d="M14 5l5 5-9 9-5-5z" />
          <path d="M5 19l-2 2" />
          <path d="M19 5l2-2" />
        </svg>
      )
    case 'target':
      return (
        <svg {...svgProps}>
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="5" />
          <circle cx="12" cy="12" r="1.5" fill={color} />
        </svg>
      )
    case 'chart':
      return (
        <svg {...svgProps}>
          <line x1="4" y1="20" x2="20" y2="20" />
          <polyline points="6 16 10 11 14 14 18 8" />
        </svg>
      )
    case 'history':
      return (
        <svg {...svgProps}>
          <path d="M3 3v7h7" />
          <path d="M3 10a9 9 0 1 0 3-6" />
        </svg>
      )
    case 'retry':
      return (
        <svg {...svgProps}>
          <polyline points="23 4 23 10 17 10" />
          <path d="M20.49 15A9 9 0 1 1 18 6.36" />
        </svg>
      )
    case 'flame':
      return (
        <svg {...svgProps}>
          <path d="M12 22 C 7 22 4 18 4 14 C 4 10 8 7 9 4 C 10 7 12 8 13 10 C 14 7 16 6 18 6 C 19 9 20 11 20 14 C 20 18 17 22 12 22 z" />
        </svg>
      )
    case 'star':
      return (
        <svg {...svgProps}>
          <polygon points="12 2 15 9 22 9 17 14 19 21 12 17 5 21 7 14 2 9 9 9" />
        </svg>
      )
    case 'close':
      return (
        <svg {...svgProps}>
          <line x1="6" y1="6" x2="18" y2="18" />
          <line x1="18" y1="6" x2="6" y2="18" />
        </svg>
      )
    case 'plus':
      return (
        <svg {...svgProps}>
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      )
    case 'warn':
      return (
        <svg {...svgProps}>
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <circle cx="12" cy="17" r=".8" fill={color} />
        </svg>
      )
  }
}
